import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  PiggyBank, Wallet, Banknote, Clock, AlertTriangle, CheckCircle2, XCircle,
  Plus, Send, Upload, Download, Eye, FileText, Calendar as CalIcon,
  ArrowDownCircle, ArrowUpCircle, RefreshCw, Search, Filter, ShieldCheck,
  Loader2, MapPin, Building2, Receipt, History
} from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';
import api, { formatErrorMessage } from '../utils/api';

const fmtCLP = (n) => {
  if (n == null || isNaN(n)) return '$ 0';
  return '$ ' + Number(n).toLocaleString('es-CL', { maximumFractionDigits: 0 });
};

const fmtFecha = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-CL', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  } catch { return '—'; }
};

const ESTADO_COLORS = {
  'Solicitada': 'bg-amber-100 text-amber-800 border-amber-300',
  'Aprobada': 'bg-blue-100 text-blue-800 border-blue-300',
  'Rechazada': 'bg-red-100 text-red-800 border-red-300',
  'Transferida': 'bg-indigo-100 text-indigo-800 border-indigo-300',
  'Por Rendir': 'bg-violet-100 text-violet-800 border-violet-300',
  'Rendida y Cerrada': 'bg-emerald-100 text-emerald-800 border-emerald-300',
  'Pendiente Devolución Funcionario': 'bg-orange-100 text-orange-800 border-orange-300',
  'Pendiente Reembolso Empresa': 'bg-pink-100 text-pink-800 border-pink-300',
  'Cerrado': 'bg-slate-200 text-slate-800 border-slate-300',
};

const EstadoBadge = ({ estado, overdue }) => (
  <Badge className={`${ESTADO_COLORS[estado] || 'bg-slate-100 text-slate-700'} border text-xs font-semibold`}>
    {overdue && <AlertTriangle className='w-3 h-3 mr-1 inline' />}
    {estado}
  </Badge>
);

export default function CajaChica() {
  const [metadata, setMetadata] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [solicitudes, setSolicitudes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('mis-solicitudes');
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [busqueda, setBusqueda] = useState('');

  const [showSolicitarModal, setShowSolicitarModal] = useState(false);
  const [selectedSolicitud, setSelectedSolicitud] = useState(null);

  const esAprobador = metadata?.es_aprobador;
  const esSupervisor = metadata?.es_supervisor;
  // Cualquiera de los dos roles (Débora ≤10k, Gema >10k) tiene bandeja y vista global
  const tieneBandeja = esAprobador || esSupervisor;
  const tieneVistaGlobal = esAprobador || esSupervisor;

  const cargarMetadata = useCallback(async () => {
    try {
      const { data } = await api.get('/caja-chica/metadata');
      setMetadata(data);
    } catch (e) {
      toast.error('Error cargando configuración: ' + formatErrorMessage(e));
    }
  }, []);

  const cargarDashboard = useCallback(async () => {
    try {
      const { data } = await api.get('/caja-chica/dashboard');
      setDashboard(data);
    } catch (e) {
      console.error('Dashboard err:', e);
    }
  }, []);

  const cargarSolicitudes = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (tab === 'mis-solicitudes') params.mias = true;
      if (filtroEstado !== 'todos') params.estado = filtroEstado;
      const { data } = await api.get('/caja-chica/solicitudes', { params });
      let items = data.items || [];
      if (busqueda) {
        const q = busqueda.toLowerCase();
        items = items.filter(s =>
          (s.motivo || '').toLowerCase().includes(q) ||
          (s.user_name || '').toLowerCase().includes(q) ||
          (s.categoria || '').toLowerCase().includes(q)
        );
      }
      setSolicitudes(items);
    } catch (e) {
      toast.error('Error cargando solicitudes: ' + formatErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [tab, filtroEstado, busqueda]);

  useEffect(() => {
    cargarMetadata();
    cargarDashboard();
  }, [cargarMetadata, cargarDashboard]);

  useEffect(() => {
    cargarSolicitudes();
  }, [cargarSolicitudes]);

  const refrescar = () => {
    cargarDashboard();
    cargarSolicitudes();
  };

  if (!metadata) {
    return (
      <div className='flex items-center justify-center py-16'>
        <Loader2 className='w-8 h-8 animate-spin text-primary' />
      </div>
    );
  }

  return (
    <div className='space-y-6' data-testid='caja-chica-module'>
      {/* HEADER */}
      <div className='flex items-center justify-between flex-wrap gap-4'>
        <div>
          <h2 className='text-2xl font-bold text-slate-900 flex items-center gap-2'>
            <PiggyBank className='w-6 h-6 text-primary' />
            Caja Chica y Fondos por Rendir
          </h2>
          <p className='text-sm text-slate-500 mt-1'>
            Solicitudes, aprobaciones y rendiciones digitales. Ruta automática:
            <span className='font-medium text-slate-700'> ≤ {fmtCLP(metadata.umbral_mp_itau)} Mercado Pago · &gt; {fmtCLP(metadata.umbral_mp_itau)} Banco Itaú</span>
          </p>
        </div>
        <div className='flex items-center gap-2'>
          <Button variant='outline' size='sm' onClick={refrescar} data-testid='caja-refresh-btn'>
            <RefreshCw className='w-4 h-4 mr-1' /> Refrescar
          </Button>
          <Button onClick={() => setShowSolicitarModal(true)} data-testid='caja-solicitar-btn'>
            <Plus className='w-4 h-4 mr-1' /> Solicitar Fondo
          </Button>
        </div>
      </div>

      {/* DASHBOARD CARDS */}
      <DashboardCards dashboard={dashboard} tieneVistaGlobal={tieneVistaGlobal} />

      {/* TABS */}
      <Tabs value={tab} onValueChange={setTab} className='w-full'>
        <TabsList className='bg-slate-100 p-1 flex-wrap h-auto'>
          <TabsTrigger value='mis-solicitudes' data-testid='caja-tab-mis'>
            <Wallet className='w-4 h-4 mr-1' /> Mis solicitudes
          </TabsTrigger>
          {tieneBandeja && (
            <TabsTrigger value='bandeja' data-testid='caja-tab-bandeja'>
              <ShieldCheck className='w-4 h-4 mr-1' /> Bandeja de Aprobación
            </TabsTrigger>
          )}
          {tieneVistaGlobal && (
            <TabsTrigger value='global' data-testid='caja-tab-global'>
              <Building2 className='w-4 h-4 mr-1' /> Vista Global
            </TabsTrigger>
          )}
        </TabsList>

        <div className='mt-4 flex items-center gap-2 flex-wrap'>
          <div className='relative flex-1 min-w-[220px] max-w-md'>
            <Search className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400' />
            <Input
              placeholder='Buscar por motivo, funcionario, categoría...'
              value={busqueda}
              onChange={e => setBusqueda(e.target.value)}
              className='pl-9'
              data-testid='caja-search-input'
            />
          </div>
          <Select value={filtroEstado} onValueChange={setFiltroEstado}>
            <SelectTrigger className='w-[200px]' data-testid='caja-filter-estado'>
              <Filter className='w-4 h-4 mr-1 inline' />
              <SelectValue placeholder='Estado' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='todos'>Todos los estados</SelectItem>
              {metadata.estados.map(e => (
                <SelectItem key={e} value={e}>{e}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <TabsContent value='mis-solicitudes' className='mt-4'>
          <SolicitudesTable
            solicitudes={solicitudes}
            loading={loading}
            onOpen={setSelectedSolicitud}
            showFuncionario={false}
          />
        </TabsContent>

        {tieneBandeja && (
          <TabsContent value='bandeja' className='mt-4'>
            <BandejaAprobacion
              solicitudes={solicitudes.filter(s =>
                ['Solicitada', 'Aprobada', 'Pendiente Devolución Funcionario', 'Pendiente Reembolso Empresa'].includes(s.estado)
              )}
              loading={loading}
              onOpen={setSelectedSolicitud}
              metadata={metadata}
            />
          </TabsContent>
        )}

        {tieneVistaGlobal && (
          <TabsContent value='global' className='mt-4'>
            <SolicitudesTable
              solicitudes={solicitudes}
              loading={loading}
              onOpen={setSelectedSolicitud}
              showFuncionario={true}
            />
          </TabsContent>
        )}
      </Tabs>

      {/* MODALES */}
      {showSolicitarModal && (
        <SolicitarFondoModal
          open={showSolicitarModal}
          onClose={() => setShowSolicitarModal(false)}
          metadata={metadata}
          onCreated={() => { refrescar(); setShowSolicitarModal(false); }}
        />
      )}

      {selectedSolicitud && (
        <SolicitudDetailModal
          open={!!selectedSolicitud}
          sid={selectedSolicitud.id}
          metadata={metadata}
          onClose={() => setSelectedSolicitud(null)}
          onChanged={() => { refrescar(); }}
        />
      )}
    </div>
  );
}

// ============= DASHBOARD CARDS =============
function DashboardCards({ dashboard, tieneVistaGlobal }) {
  if (!dashboard) return null;
  const cards = [
    {
      label: 'En la Calle (Por Rendir)',
      value: fmtCLP(dashboard.en_la_calle?.total || 0),
      sub: `MP Central ${fmtCLP(dashboard.en_la_calle?.Central_MP || 0)} · MP Buin ${fmtCLP(dashboard.en_la_calle?.Buin_MP || 0)} · Itaú ${fmtCLP(dashboard.en_la_calle?.Itau || 0)}`,
      icon: <Wallet className='w-5 h-5' />,
      gradient: 'from-violet-500 to-purple-600',
      testid: 'caja-card-enlacalle'
    },
    {
      label: 'Pendientes de Aprobación',
      value: dashboard.pendientes_aprobacion,
      sub: 'Esperando a Débora',
      icon: <ShieldCheck className='w-5 h-5' />,
      gradient: 'from-amber-500 to-orange-600',
      testid: 'caja-card-aprobacion'
    },
    {
      label: 'Por Transferir',
      value: dashboard.pendientes_transferencia,
      sub: 'Aprobadas, falta giro',
      icon: <Send className='w-5 h-5' />,
      gradient: 'from-blue-500 to-indigo-600',
      testid: 'caja-card-transferir'
    },
    {
      label: 'Rendiciones Vencidas',
      value: dashboard.rendiciones_overdue,
      sub: '> 3 días hábiles',
      icon: <AlertTriangle className='w-5 h-5' />,
      gradient: dashboard.rendiciones_overdue > 0 ? 'from-red-500 to-rose-600' : 'from-slate-400 to-slate-500',
      testid: 'caja-card-overdue'
    },
  ];

  return (
    <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4'>
      {cards.map((c, i) => (
        <Card key={i} className='p-4 border-slate-200 hover:shadow-md transition-shadow' data-testid={c.testid}>
          <div className='flex items-start justify-between mb-2'>
            <div className={`p-2 rounded-lg bg-gradient-to-br ${c.gradient} text-white shadow-sm`}>
              {c.icon}
            </div>
          </div>
          <p className='text-xs text-slate-500 font-medium'>{c.label}</p>
          <p className='text-2xl font-bold text-slate-900 mt-1'>{c.value}</p>
          <p className='text-[10px] text-slate-400 mt-1 leading-tight'>{c.sub}</p>
        </Card>
      ))}
    </div>
  );
}

// ============= TABLA DE SOLICITUDES =============
function SolicitudesTable({ solicitudes, loading, onOpen, showFuncionario }) {
  if (loading) {
    return (
      <div className='flex items-center justify-center py-12'>
        <Loader2 className='w-6 h-6 animate-spin text-primary' />
      </div>
    );
  }
  if (solicitudes.length === 0) {
    return (
      <Card className='p-12 text-center border-slate-200 border-dashed'>
        <Receipt className='w-12 h-12 text-slate-300 mx-auto mb-3' />
        <p className='text-slate-500'>No hay solicitudes con esos filtros.</p>
      </Card>
    );
  }
  return (
    <Card className='border-slate-200 overflow-x-auto'>
      <table className='w-full text-sm'>
        <thead className='bg-slate-50 text-slate-600 text-xs uppercase'>
          <tr>
            <th className='text-left px-4 py-3'>Fecha</th>
            {showFuncionario && <th className='text-left px-4 py-3'>Funcionario</th>}
            <th className='text-left px-4 py-3'>Motivo</th>
            <th className='text-left px-4 py-3'>Categoría</th>
            <th className='text-right px-4 py-3'>Monto</th>
            <th className='text-left px-4 py-3'>Vía</th>
            <th className='text-left px-4 py-3'>Estado</th>
            <th className='text-left px-4 py-3'>Deadline</th>
            <th className='text-right px-4 py-3'></th>
          </tr>
        </thead>
        <tbody>
          {solicitudes.map(s => (
            <tr key={s.id} className='border-t border-slate-100 hover:bg-slate-50/60' data-testid={`caja-row-${s.id}`}>
              <td className='px-4 py-3 text-slate-600 whitespace-nowrap'>{fmtFecha(s.created_at)}</td>
              {showFuncionario && (
                <td className='px-4 py-3 text-slate-700'>
                  {s.user_name}
                  <div className='text-[10px] text-slate-400'>{s.sucursal}</div>
                </td>
              )}
              <td className='px-4 py-3 text-slate-700 max-w-[220px] truncate' title={s.motivo}>{s.motivo}</td>
              <td className='px-4 py-3 text-slate-600'>{s.categoria}</td>
              <td className='px-4 py-3 text-right font-semibold text-slate-900'>{fmtCLP(s.monto_clp)}</td>
              <td className='px-4 py-3'>
                <Badge variant='outline' className='text-xs'>{s.via_pago}</Badge>
              </td>
              <td className='px-4 py-3'>
                <EstadoBadge estado={s.estado} overdue={s.overdue} />
              </td>
              <td className='px-4 py-3 text-xs text-slate-600'>
                {s.estado === 'Por Rendir' && s.horas_restantes != null ? (
                  <span className={s.horas_restantes < 0 ? 'text-red-600 font-semibold' : s.horas_restantes < 12 ? 'text-amber-600 font-semibold' : 'text-slate-600'}>
                    {s.horas_restantes < 0 ? `Vencida hace ${Math.abs(s.horas_restantes).toFixed(0)}h` : `${s.horas_restantes.toFixed(0)}h restantes`}
                  </span>
                ) : '—'}
              </td>
              <td className='px-4 py-3 text-right'>
                <Button size='sm' variant='outline' onClick={() => onOpen(s)} data-testid={`caja-open-${s.id}`}>
                  <Eye className='w-3.5 h-3.5 mr-1' /> Abrir
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

// ============= BANDEJA APROBACIÓN (Débora ≤10k · Gema >10k) =============
function BandejaAprobacion({ solicitudes, loading, onOpen, metadata }) {
  // Filtrar solo las que ME tocan según el monto y mi email
  const myEmail = useMemo(() => {
    try { return (JSON.parse(localStorage.getItem('user') || '{}')).email || ''; } catch { return ''; }
  }, []);
  const umbral = metadata?.umbral_mp_itau || 10000;
  const emailLow = metadata?.aprobadores?.menor_o_igual_umbral?.email;
  const emailHigh = metadata?.aprobadores?.mayor_a_umbral?.email;

  const misSolicitudes = useMemo(() => {
    return (solicitudes || []).filter(s => {
      const m = Number(s.monto_clp || 0);
      const aprob = m <= umbral ? emailLow : emailHigh;
      return aprob === myEmail;
    });
  }, [solicitudes, myEmail, umbral, emailLow, emailHigh]);

  const grupos = useMemo(() => {
    const g = { 'Solicitada': [], 'Aprobada': [], 'Pendiente Devolución Funcionario': [], 'Pendiente Reembolso Empresa': [] };
    misSolicitudes.forEach(s => { if (g[s.estado]) g[s.estado].push(s); });
    return g;
  }, [misSolicitudes]);

  if (loading) {
    return <div className='flex items-center justify-center py-12'><Loader2 className='w-6 h-6 animate-spin text-primary' /></div>;
  }

  const colDef = [
    { key: 'Solicitada', label: 'Solicitada (Aprobar)', color: 'border-amber-300 bg-amber-50' },
    { key: 'Aprobada', label: 'Aprobada (Transferir)', color: 'border-blue-300 bg-blue-50' },
    { key: 'Pendiente Devolución Funcionario', label: 'Devolución (Validar)', color: 'border-orange-300 bg-orange-50' },
    { key: 'Pendiente Reembolso Empresa', label: 'Reembolso (Pagar)', color: 'border-pink-300 bg-pink-50' },
  ];

  return (
    <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4'>
      {colDef.map(col => (
        <Card key={col.key} className={`p-3 border ${col.color}`} data-testid={`caja-col-${col.key.replace(/\s+/g, '-')}`}>
          <div className='flex items-center justify-between mb-3'>
            <h3 className='text-sm font-semibold text-slate-800'>{col.label}</h3>
            <Badge variant='secondary'>{grupos[col.key].length}</Badge>
          </div>
          <div className='space-y-2 max-h-[60vh] overflow-y-auto'>
            {grupos[col.key].length === 0 && (
              <p className='text-xs text-slate-400 text-center py-6'>Sin pendientes</p>
            )}
            {grupos[col.key].map(s => (
              <div
                key={s.id}
                onClick={() => onOpen(s)}
                className='bg-white p-3 rounded-md border border-slate-200 cursor-pointer hover:shadow-sm transition'
                data-testid={`caja-card-${s.id}`}
              >
                <div className='flex items-start justify-between gap-2'>
                  <p className='text-sm font-medium text-slate-800 truncate flex-1' title={s.user_name}>{s.user_name}</p>
                  <span className='text-xs font-bold text-slate-900 whitespace-nowrap'>{fmtCLP(s.monto_clp)}</span>
                </div>
                <p className='text-xs text-slate-500 mt-1 truncate' title={s.motivo}>{s.motivo}</p>
                <div className='flex items-center justify-between mt-2'>
                  <Badge variant='outline' className='text-[10px]'>{s.via_pago}</Badge>
                  <span className='text-[10px] text-slate-400'>{fmtFecha(s.created_at)}</span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      ))}
    </div>
  );
}

// ============= MODAL SOLICITAR FONDO =============
function SolicitarFondoModal({ open, onClose, metadata, onCreated }) {
  const guardado = metadata.datos_bancarios_guardados || {};
  const [monto, setMonto] = useState('');
  const [motivo, setMotivo] = useState('');
  const [categoria, setCategoria] = useState(metadata.categorias[0]);
  const [sucursal, setSucursal] = useState(metadata.sucursal_usuario);
  const [rut, setRut] = useState(guardado.rut || '');
  const [banco, setBanco] = useState(guardado.banco || '');
  const [tipoCuenta, setTipoCuenta] = useState(guardado.tipo_cuenta || 'Cuenta Vista');
  const [numeroCuenta, setNumeroCuenta] = useState(guardado.numero_cuenta || '');
  const [enviando, setEnviando] = useState(false);

  const montoNum = Number(monto) || 0;
  const fuentePreview = montoNum <= metadata.umbral_mp_itau
    ? `Mercado Pago - ${sucursal}`
    : 'Banco Itaú';

  const submit = async () => {
    if (montoNum <= 0) { toast.error('Ingresa un monto válido'); return; }
    if (!motivo.trim() || motivo.trim().length < 3) { toast.error('Motivo mínimo 3 caracteres'); return; }
    if (!rut.trim() || !banco.trim() || !numeroCuenta.trim()) {
      toast.error('Completa datos bancarios (RUT, banco, número de cuenta)');
      return;
    }
    setEnviando(true);
    try {
      await api.post('/caja-chica/solicitudes', {
        monto_clp: montoNum,
        motivo: motivo.trim(),
        categoria,
        sucursal_override: sucursal,
        datos_bancarios: { rut: rut.trim(), banco: banco.trim(), tipo_cuenta: tipoCuenta, numero_cuenta: numeroCuenta.trim() }
      });
      toast.success('Solicitud creada. Notificamos a Débora.');
      onCreated();
    } catch (e) {
      toast.error('Error: ' + formatErrorMessage(e));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className='max-w-2xl' data-testid='caja-modal-solicitar'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <Plus className='w-5 h-5 text-primary' /> Solicitar Fondo
          </DialogTitle>
          <DialogDescription>
            Si el monto es ≤ {fmtCLP(metadata.umbral_mp_itau)} se transfiere por <b>Mercado Pago</b>, de lo contrario por <b>Banco Itaú</b>.
          </DialogDescription>
        </DialogHeader>

        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          <div>
            <Label>Monto (CLP) *</Label>
            <Input type='number' value={monto} onChange={e => setMonto(e.target.value)}
              placeholder='Ej. 15000' data-testid='caja-input-monto' />
            {montoNum > 0 && (
              <p className='text-xs text-slate-500 mt-1'>
                Se enrutará por: <b className='text-slate-700'>{fuentePreview}</b>
              </p>
            )}
          </div>
          <div>
            <Label>Sucursal</Label>
            <Select value={sucursal} onValueChange={setSucursal}>
              <SelectTrigger data-testid='caja-input-sucursal'><SelectValue /></SelectTrigger>
              <SelectContent>
                {metadata.sucursales.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className='md:col-span-2'>
            <Label>Motivo del gasto *</Label>
            <Textarea value={motivo} onChange={e => setMotivo(e.target.value)} rows={2}
              placeholder='Ej. Compra de tóner para impresora Oficina Central'
              data-testid='caja-input-motivo' />
          </div>
          <div className='md:col-span-2'>
            <Label>Categoría</Label>
            <Select value={categoria} onValueChange={setCategoria}>
              <SelectTrigger data-testid='caja-input-categoria'><SelectValue /></SelectTrigger>
              <SelectContent>
                {metadata.categorias.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className='md:col-span-2 pt-2 border-t border-slate-100'>
            <p className='text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1'>
              <Banknote className='w-4 h-4' /> Datos Bancarios (donde recibir el fondo)
            </p>
          </div>
          <div>
            <Label>RUT *</Label>
            <Input value={rut} onChange={e => setRut(e.target.value)} placeholder='12.345.678-9' data-testid='caja-input-rut' />
          </div>
          <div>
            <Label>Banco *</Label>
            <Input value={banco} onChange={e => setBanco(e.target.value)} placeholder='Banco Estado' data-testid='caja-input-banco' />
          </div>
          <div>
            <Label>Tipo de Cuenta</Label>
            <Select value={tipoCuenta} onValueChange={setTipoCuenta}>
              <SelectTrigger data-testid='caja-input-tipo-cuenta'><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value='Cuenta Corriente'>Cuenta Corriente</SelectItem>
                <SelectItem value='Cuenta Vista'>Cuenta Vista</SelectItem>
                <SelectItem value='Cuenta RUT'>Cuenta RUT</SelectItem>
                <SelectItem value='Cuenta Ahorro'>Cuenta Ahorro</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Nº de cuenta *</Label>
            <Input value={numeroCuenta} onChange={e => setNumeroCuenta(e.target.value)} placeholder='000123456789' data-testid='caja-input-numero-cuenta' />
          </div>
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={enviando} data-testid='caja-submit-solicitar'>
            {enviando ? <><Loader2 className='w-4 h-4 mr-1 animate-spin' /> Enviando...</> : <><Send className='w-4 h-4 mr-1' /> Enviar Solicitud</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============= MODAL DETALLE / ACCIONES =============
function SolicitudDetailModal({ open, sid, metadata, onClose, onChanged }) {
  const [solicitud, setSolicitud] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);

  const [motivoRechazo, setMotivoRechazo] = useState('');
  const [notaTransfer, setNotaTransfer] = useState('');

  const [rendMonto, setRendMonto] = useState('');
  const [rendDesc, setRendDesc] = useState('');
  const [rendFile, setRendFile] = useState(null);

  const [devMonto, setDevMonto] = useState('');
  const [devFile, setDevFile] = useState(null);

  // Email del usuario logueado para chequear si es el aprobador correcto según monto
  const myEmail = useMemo(() => {
    try { return (JSON.parse(localStorage.getItem('user') || '{}')).email || ''; } catch { return ''; }
  }, []);
  // Email del aprobador real de ESTA solicitud (según su monto)
  const aprobadorEmail = useMemo(() => {
    if (!solicitud || !metadata?.aprobadores) return '';
    const monto = Number(solicitud.monto_clp || 0);
    return monto <= (metadata.umbral_mp_itau || 10000)
      ? metadata.aprobadores.menor_o_igual_umbral.email
      : metadata.aprobadores.mayor_a_umbral.email;
  }, [solicitud, metadata]);
  const aprobadorLabel = useMemo(() => {
    if (!solicitud || !metadata?.aprobadores) return '';
    const monto = Number(solicitud.monto_clp || 0);
    return monto <= (metadata.umbral_mp_itau || 10000)
      ? metadata.aprobadores.menor_o_igual_umbral.label
      : metadata.aprobadores.mayor_a_umbral.label;
  }, [solicitud, metadata]);
  const soyAprobadorDeEsta = myEmail && aprobadorEmail && myEmail === aprobadorEmail;

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/caja-chica/solicitudes/${sid}`);
      setSolicitud(data);
    } catch (e) {
      toast.error('Error: ' + formatErrorMessage(e));
      onClose();
    } finally { setLoading(false); }
  }, [sid, onClose]);

  useEffect(() => { if (sid) cargar(); }, [sid, cargar]);

  const doAction = async (path, body, opts = {}) => {
    setActing(true);
    try {
      await api.post(`/caja-chica/solicitudes/${sid}/${path}`, body, opts);
      toast.success('Acción completada');
      onChanged();
      cargar();
    } catch (e) {
      toast.error('Error: ' + formatErrorMessage(e));
    } finally { setActing(false); }
  };

  const aprobar = () => doAction('aprobar', {});
  const rechazar = () => {
    if (!motivoRechazo.trim()) { toast.error('Motivo obligatorio'); return; }
    doAction('rechazar', { motivo_rechazo: motivoRechazo.trim() });
  };
  const transferir = () => doAction('transferir', { nota: notaTransfer.trim() });
  const subirRendicion = async () => {
    const m = Number(rendMonto) || 0;
    if (m <= 0) { toast.error('Monto inválido'); return; }
    if (!rendDesc.trim() || rendDesc.trim().length < 3) { toast.error('Descripción mínimo 3 chars'); return; }
    if (!rendFile) { toast.error('Sube un comprobante (PDF o imagen)'); return; }
    const fd = new FormData();
    fd.append('monto_clp', m);
    fd.append('descripcion', rendDesc.trim());
    fd.append('file', rendFile);
    setActing(true);
    try {
      await api.post(`/caja-chica/solicitudes/${sid}/rendir-item`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Comprobante agregado');
      setRendMonto(''); setRendDesc(''); setRendFile(null);
      onChanged(); cargar();
    } catch (e) {
      toast.error('Error: ' + formatErrorMessage(e));
    } finally { setActing(false); }
  };
  const cerrarRendicion = () => doAction('cerrar-rendicion', {});

  const subirDevolucion = async () => {
    const m = Number(devMonto) || 0;
    if (m <= 0) { toast.error('Monto inválido'); return; }
    if (!devFile) { toast.error('Sube comprobante de transferencia'); return; }
    const fd = new FormData();
    fd.append('monto_clp', m);
    fd.append('file', devFile);
    setActing(true);
    try {
      await api.post(`/caja-chica/solicitudes/${sid}/devolucion-comprobante`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Comprobante de devolución cargado');
      setDevMonto(''); setDevFile(null);
      onChanged(); cargar();
    } catch (e) {
      toast.error('Error: ' + formatErrorMessage(e));
    } finally { setActing(false); }
  };
  const validarDevolucion = () => doAction('validar-devolucion', {});
  const transferirReembolso = () => doAction('transferir-reembolso', {});

  const descargarComprobante = async (rid, filename) => {
    try {
      const resp = await api.get(`/caja-chica/solicitudes/${sid}/rendiciones/${rid}/file`, { responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url; a.download = filename || 'comprobante';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Error descargando');
    }
  };

  if (!solicitud && !loading) return null;

  const totalRendido = (solicitud?.rendiciones || []).reduce((acc, r) => acc + Number(r.monto_clp || 0), 0);
  const diferencia = solicitud ? Number(solicitud.monto_clp) - totalRendido : 0;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className='max-w-4xl max-h-[90vh] overflow-y-auto' data-testid='caja-modal-detalle'>
        {loading || !solicitud ? (
          <div className='flex items-center justify-center py-12'>
            <Loader2 className='w-6 h-6 animate-spin text-primary' />
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className='flex items-center gap-2 flex-wrap'>
                <Receipt className='w-5 h-5 text-primary' />
                Solicitud de {solicitud.user_name}
                <EstadoBadge estado={solicitud.estado} overdue={solicitud.overdue} />
              </DialogTitle>
              <DialogDescription>{solicitud.motivo}</DialogDescription>
            </DialogHeader>

            {/* META */}
            <div className='grid grid-cols-2 md:grid-cols-4 gap-3 text-sm py-2 border-y border-slate-100'>
              <div>
                <p className='text-xs text-slate-500'>Monto</p>
                <p className='font-bold text-lg text-slate-900'>{fmtCLP(solicitud.monto_clp)}</p>
              </div>
              <div>
                <p className='text-xs text-slate-500'>Categoría</p>
                <p className='font-medium text-slate-800'>{solicitud.categoria}</p>
              </div>
              <div>
                <p className='text-xs text-slate-500'>Vía</p>
                <p className='font-medium text-slate-800'>{solicitud.via_pago}</p>
                <p className='text-[10px] text-slate-400'>{solicitud.sucursal}</p>
              </div>
              <div>
                <p className='text-xs text-slate-500'>Solicitado</p>
                <p className='font-medium text-slate-700 text-xs'>{fmtFecha(solicitud.created_at)}</p>
              </div>
            </div>

            {/* DATOS BANCARIOS */}
            {solicitud.datos_bancarios && (
              <div className='bg-slate-50 rounded-md p-3 text-sm border border-slate-200'>
                <p className='text-xs font-semibold text-slate-600 mb-1 flex items-center gap-1'>
                  <Banknote className='w-3.5 h-3.5' /> Datos Bancarios
                </p>
                <p className='text-slate-700 text-xs'>
                  {solicitud.datos_bancarios.banco} · {solicitud.datos_bancarios.tipo_cuenta} · Nº {solicitud.datos_bancarios.numero_cuenta} · RUT {solicitud.datos_bancarios.rut}
                </p>
              </div>
            )}

            {/* DEADLINE */}
            {solicitud.estado === 'Por Rendir' && solicitud.deadline_rendicion_at && (
              <div className={`rounded-md p-3 border ${solicitud.overdue ? 'bg-red-50 border-red-300' : 'bg-amber-50 border-amber-300'}`}>
                <p className='text-xs font-semibold flex items-center gap-1'>
                  <Clock className='w-3.5 h-3.5' />
                  {solicitud.overdue ? 'RENDICIÓN VENCIDA' : 'Plazo para rendir'}
                </p>
                <p className='text-sm text-slate-700'>
                  Hasta: <b>{fmtFecha(solicitud.deadline_rendicion_at)}</b>
                  {solicitud.horas_restantes != null && (
                    <span className='ml-2 text-xs text-slate-500'>
                      ({solicitud.horas_restantes < 0
                        ? `vencido hace ${Math.abs(solicitud.horas_restantes).toFixed(0)}h`
                        : `${solicitud.horas_restantes.toFixed(0)}h restantes`})
                    </span>
                  )}
                </p>
              </div>
            )}

            {/* RENDICIONES */}
            {(solicitud.rendiciones || []).length > 0 && (
              <div>
                <p className='text-sm font-semibold text-slate-700 mb-2'>Gastos rendidos</p>
                <div className='border rounded-md divide-y divide-slate-100'>
                  {solicitud.rendiciones.map(r => (
                    <div key={r.id} className='flex items-center justify-between p-2 text-sm'>
                      <div className='flex-1 min-w-0'>
                        <p className='font-medium text-slate-800 truncate'>{r.descripcion}</p>
                        <p className='text-[10px] text-slate-400'>{fmtFecha(r.uploaded_at)}</p>
                      </div>
                      <p className='font-semibold text-slate-900 mr-3'>{fmtCLP(r.monto_clp)}</p>
                      <Button size='sm' variant='ghost' onClick={() => descargarComprobante(r.id, r.filename)}>
                        <Download className='w-3.5 h-3.5' />
                      </Button>
                    </div>
                  ))}
                </div>
                <div className='flex justify-between mt-2 text-sm bg-slate-50 p-2 rounded'>
                  <span className='text-slate-600'>Total rendido</span>
                  <span className='font-bold'>{fmtCLP(totalRendido)} / {fmtCLP(solicitud.monto_clp)}</span>
                </div>
                {solicitud.estado === 'Por Rendir' && Math.abs(diferencia) >= 1 && (
                  <p className={`text-xs mt-1 ${diferencia > 0 ? 'text-orange-600' : 'text-pink-600'}`}>
                    {diferencia > 0
                      ? `Si cierras ahora deberás devolver ${fmtCLP(diferencia)}`
                      : `Si cierras ahora Peritum debe reembolsarte ${fmtCLP(Math.abs(diferencia))}`
                    }
                  </p>
                )}
              </div>
            )}

            {/* ACCIONES SEGÚN ESTADO/ROL */}
            <div className='space-y-3 pt-3 border-t border-slate-100'>
              {/* Aviso si soy aprobador/supervisor pero esta solicitud NO me toca */}
              {!soyAprobadorDeEsta && (metadata?.es_aprobador || metadata?.es_supervisor) &&
                ['Solicitada','Aprobada','Pendiente Devolución Funcionario','Pendiente Reembolso Empresa'].includes(solicitud.estado) && (
                <Card className='p-3 bg-slate-100 border-slate-200 text-xs text-slate-600' data-testid='caja-aviso-no-aprobador'>
                  Esta solicitud ({fmtCLP(solicitud.monto_clp)}) debe ser gestionada por <b>{aprobadorLabel} ({aprobadorEmail})</b>.
                </Card>
              )}

              {/* APROBADOR de ESTA solicitud (según monto): Solicitada -> Aprobar/Rechazar */}
              {soyAprobadorDeEsta && solicitud.estado === 'Solicitada' && (
                <Card className='p-3 bg-amber-50 border-amber-200'>
                  <p className='text-sm font-semibold mb-2'>Aprobación · {aprobadorLabel}</p>
                  <div className='flex gap-2'>
                    <Button onClick={aprobar} disabled={acting} className='bg-emerald-600 hover:bg-emerald-700' data-testid='caja-action-aprobar'>
                      <CheckCircle2 className='w-4 h-4 mr-1' /> Aprobar
                    </Button>
                  </div>
                  <div className='mt-3'>
                    <Label className='text-xs'>Motivo de rechazo</Label>
                    <Input value={motivoRechazo} onChange={e => setMotivoRechazo(e.target.value)} placeholder='Ej. Sin presupuesto este mes' data-testid='caja-input-motivo-rechazo' />
                    <Button onClick={rechazar} disabled={acting || !motivoRechazo.trim()} variant='destructive' size='sm' className='mt-2' data-testid='caja-action-rechazar'>
                      <XCircle className='w-4 h-4 mr-1' /> Rechazar
                    </Button>
                  </div>
                </Card>
              )}

              {/* APROBADOR: Aprobada -> Transferir */}
              {soyAprobadorDeEsta && solicitud.estado === 'Aprobada' && (
                <Card className='p-3 bg-blue-50 border-blue-200'>
                  <p className='text-sm font-semibold mb-2'>Marcar como Transferida (inicia timer 3 días hábiles)</p>
                  <Label className='text-xs'>Nota (opcional)</Label>
                  <Input value={notaTransfer} onChange={e => setNotaTransfer(e.target.value)} placeholder='Nº de operación, comprobante...' data-testid='caja-input-nota-transfer' />
                  <Button onClick={transferir} disabled={acting} className='mt-2 bg-indigo-600 hover:bg-indigo-700' data-testid='caja-action-transferir'>
                    <Send className='w-4 h-4 mr-1' /> Confirmar Transferencia
                  </Button>
                </Card>
              )}

              {/* FUNCIONARIO: Por Rendir -> agregar rendición y cerrar */}
              {solicitud.estado === 'Por Rendir' && (
                <Card className='p-3 bg-violet-50 border-violet-200'>
                  <p className='text-sm font-semibold mb-2 flex items-center gap-1'>
                    <Upload className='w-4 h-4' /> Agregar comprobante
                  </p>
                  <div className='grid grid-cols-1 md:grid-cols-3 gap-2'>
                    <Input type='number' placeholder='Monto CLP' value={rendMonto} onChange={e => setRendMonto(e.target.value)} data-testid='caja-input-rend-monto' />
                    <Input placeholder='Descripción' value={rendDesc} onChange={e => setRendDesc(e.target.value)} className='md:col-span-2' data-testid='caja-input-rend-desc' />
                  </div>
                  <Input type='file' accept='.pdf,.jpg,.jpeg,.png,.webp' onChange={e => setRendFile(e.target.files?.[0])} className='mt-2' data-testid='caja-input-rend-file' />
                  <Button onClick={subirRendicion} disabled={acting} size='sm' className='mt-2' data-testid='caja-action-rendir'>
                    <Plus className='w-3.5 h-3.5 mr-1' /> Agregar gasto
                  </Button>
                  {(solicitud.rendiciones || []).length > 0 && (
                    <div className='mt-3 pt-3 border-t border-violet-200'>
                      <Button onClick={cerrarRendicion} disabled={acting} className='bg-emerald-600 hover:bg-emerald-700' data-testid='caja-action-cerrar-rendicion'>
                        <CheckCircle2 className='w-4 h-4 mr-1' /> Cerrar Rendición
                      </Button>
                      <p className='text-xs text-slate-500 mt-1'>El sistema calculará si hay calce, sobrante o faltante.</p>
                    </div>
                  )}
                </Card>
              )}

              {/* FUNCIONARIO: Pendiente Devolución -> subir comprobante */}
              {solicitud.estado === 'Pendiente Devolución Funcionario' && !solicitud.devolucion && (
                <Card className='p-3 bg-orange-50 border-orange-200'>
                  <p className='text-sm font-semibold mb-1'>Devolución de sobrante: {fmtCLP(solicitud.sobrante_clp || 0)}</p>
                  <p className='text-xs text-slate-600 mb-2'>Transfiere el sobrante a la caja origen y sube el comprobante.</p>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-2'>
                    <Input type='number' placeholder='Monto devuelto' value={devMonto} onChange={e => setDevMonto(e.target.value)} data-testid='caja-input-dev-monto' />
                    <Input type='file' accept='.pdf,.jpg,.jpeg,.png,.webp' onChange={e => setDevFile(e.target.files?.[0])} data-testid='caja-input-dev-file' />
                  </div>
                  <Button onClick={subirDevolucion} disabled={acting} size='sm' className='mt-2 bg-orange-600 hover:bg-orange-700' data-testid='caja-action-dev-upload'>
                    <ArrowUpCircle className='w-3.5 h-3.5 mr-1' /> Subir comprobante de devolución
                  </Button>
                </Card>
              )}

              {/* APROBADOR: validar devolución */}
              {soyAprobadorDeEsta && solicitud.estado === 'Pendiente Devolución Funcionario' && solicitud.devolucion && (
                <Card className='p-3 bg-orange-50 border-orange-200'>
                  <p className='text-sm font-semibold mb-1'>Validar devolución de {fmtCLP(solicitud.devolucion.monto_clp)}</p>
                  <p className='text-xs text-slate-600 mb-2'>Comprobante cargado: {solicitud.devolucion.filename}</p>
                  <Button onClick={validarDevolucion} disabled={acting} className='bg-emerald-600 hover:bg-emerald-700' data-testid='caja-action-validar-dev'>
                    <ShieldCheck className='w-4 h-4 mr-1' /> Validar y Cerrar
                  </Button>
                </Card>
              )}

              {/* APROBADOR: reembolso */}
              {soyAprobadorDeEsta && solicitud.estado === 'Pendiente Reembolso Empresa' && (
                <Card className='p-3 bg-pink-50 border-pink-200'>
                  <p className='text-sm font-semibold mb-1'>Reembolsar al funcionario: {fmtCLP(solicitud.faltante_clp || 0)}</p>
                  <p className='text-xs text-slate-600 mb-2'>Vía sugerida: <b>{solicitud.reembolso_via}</b></p>
                  <Button onClick={transferirReembolso} disabled={acting} className='bg-pink-600 hover:bg-pink-700' data-testid='caja-action-reembolso'>
                    <ArrowDownCircle className='w-4 h-4 mr-1' /> Confirmar Reembolso
                  </Button>
                </Card>
              )}
            </div>

            {/* HISTORIAL */}
            {(solicitud.historial || []).length > 0 && (
              <div className='pt-3 border-t border-slate-100'>
                <p className='text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1'>
                  <History className='w-4 h-4' /> Historial
                </p>
                <div className='space-y-1.5 text-xs'>
                  {solicitud.historial.slice().reverse().map((h, i) => (
                    <div key={i} className='flex items-start gap-2 text-slate-600'>
                      <span className='text-slate-400 whitespace-nowrap'>{fmtFecha(h.at)}</span>
                      <span className='flex-1'>
                        <b className='text-slate-700'>{h.accion}</b>
                        <span className='text-slate-500'> · {h.by}</span>
                        {h.notas && <p className='text-[10px] text-slate-500 italic'>{h.notas}</p>}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant='outline' onClick={onClose}>Cerrar</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
